import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useNavigate } from "react-router-dom";
import ComponentRow from "./ComponentRow";
import { get, post, del, put } from "src/utils/networkUtils";

export default function HomeComponentsPage() {
  const navigate = useNavigate();
  const [components, setComponents] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState(null);

  const handleGetHomeComponents = () => {
    get(`/admin/home-components`).then(function (response) {
      if (response.homeComponents.length) {
        setComponents(response.homeComponents);
      } else {
        setComponents([]);
      }
    });
  };

  useEffect(() => {
    handleGetHomeComponents();
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (+active?.id !== +over?.id) {
      setComponents((items) => {
        const oldIndex = items.findIndex(
          (item) => +item.homeComponentId === +active?.id
        );
        const newIndex = items.findIndex(
          (item) => +item.homeComponentId === +over?.id
        );
        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        return reorderedItems.map((item, index) => ({
          ...item,
          homeComponentOrder: index,
        }));
      });
    }
  };

  const handleUpdate = (id, updatedComponent) => {
    setComponents((prev) =>
      prev.map((comp) =>
        comp.homeComponentId === id ? updatedComponent : comp
      )
    );
    if (editingId === id) {
      setEditingId(null);
    }
  };

  const UpdateHandler = async (id, updatedComponent) => {
    try {
      setUpdating(true);
      const response = await put(
        `/admin/home-components/${id}`,
        updatedComponent
      );
      if (response.success) {
        handleGetHomeComponents();
      }
    } catch (error) {
      console.error("Error updating component:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = (id) => {
    setComponentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!componentToDelete) return;
    setDeleteDialogOpen(false);
    try {
      setDeleting(true);
      const response = await del(`/admin/home-components/${componentToDelete}`);
      if (response.success) {
        handleGetHomeComponents();
        setComponentToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting component:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setComponentToDelete(null);
  };

  const handleEdit = (id) => {
    const component = components.find((c) => c.homeComponentId === id);
    if (component) {
      navigate(`/dashboard/home-components/${id}?type=${component.type}`);
    }
  };

  const handleAddNew = () => {
    setComponents((prev) => {
      // Find the maximum homeComponentOrder and add 1
      const maxOrder =
        prev.length > 0
          ? Math.max(...prev.map((c) => c.homeComponentOrder ?? 0))
          : -1;

      const newComponent = {
        homeComponentId: Date.now().toString(),
        type: 0,
        visible: false,
        homeComponentOrder: maxOrder + 1,
      };
      return [...prev, newComponent];
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await post(`/admin/home-components/bulk-upsert`, {
        homeComponents: components,
      });
      if (response.success) {
        handleGetHomeComponents();
      }
    } catch (error) {
      console.error("Error saving components:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    handleGetHomeComponents();
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: "31.429px",
              color: "#000",
            }}
          >
            Home Components
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              disabled={saving || deleting || updating}
              sx={{
                backgroundColor: "#3167ca",
                color: "white",
                fontWeight: 700,
                fontSize: "16px",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                px: 3,
                py: 1.5,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#2555a8",
                },
              }}
            >
              Add New Component
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving || deleting || updating}
              sx={{
                borderColor: "#3167ca",
                color: "#3167ca",
                fontWeight: 700,
                fontSize: "17.143px",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                minWidth: "107px",
                height: "47px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#3167ca",
                  backgroundColor: "rgba(49, 103, 202, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
              disabled={saving || deleting || updating}
              sx={{
                borderColor: "#3167ca",
                color: "#3167ca",
                fontWeight: 700,
                fontSize: "17.143px",
                borderRadius: "8px",
                boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                minWidth: "107px",
                height: "47px",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#3167ca",
                  backgroundColor: "rgba(49, 103, 202, 0.04)",
                },
              }}
            >
              {saving ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </Stack>
        </Box>

        {/* Table Header */}
        <Paper
          sx={{
            backgroundColor: "#F5F6F8",
            borderRadius: "16px",
            p: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
            }}
          >
            <Box sx={{ width: "8.33%" }} /> {/* Spacer for drag handle */}
            <Typography
              sx={{
                width: "25%",
                fontWeight: 700,
                fontSize: "15px",
                color: "#687581",
              }}
            >
              Type
            </Typography>
            <Typography
              sx={{
                width: "16.67%",
                fontWeight: 700,
                fontSize: "15px",
                color: "#687581",
              }}
            >
              Visible
            </Typography>
            <Box sx={{ width: "25%" }} /> {/* Spacer for title */}
            <Box sx={{ width: "25%" }} /> {/* Spacer for actions */}
          </Box>
        </Paper>

        {/* Components List */}
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={components.map((c) => c.homeComponentId)}
            strategy={verticalListSortingStrategy}
          >
            {components
              .sort(
                (a, b) =>
                  (a.homeComponentOrder ?? 0) - (b.homeComponentOrder ?? 0)
              )
              .map((component) => (
                <ComponentRow
                  key={component.homeComponentId}
                  component={component}
                  onUpdate={handleUpdate}
                  onDelete={handleDeleteClick}
                  onEdit={handleEdit}
                  onUpdateHandler={UpdateHandler}
                  isEditing={editingId === component.homeComponentId}
                  disabled={saving || deleting || updating}
                />
              ))}
          </SortableContext>
        </DndContext>

        {/* Add New Component Button at Bottom */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            disabled={saving || deleting || updating}
            sx={{
              backgroundColor: "#3167ca",
              color: "white",
              fontWeight: 700,
              fontSize: "16px",
              borderRadius: "8px",
              boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
              px: 3,
              py: 1.5,
              textTransform: "none",
              minWidth: "204px",
              "&:hover": {
                backgroundColor: "#2555a8",
              },
            }}
          >
            Add New Component
          </Button>
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Are you sure, want to delete?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              This action cannot be undone. The component will be permanently
              deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteCancel}
              sx={{
                color: "#3167ca",
                fontWeight: 600,
              }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              autoFocus
              variant="contained"
              sx={{
                backgroundColor: "#FF5274",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#e04562",
                },
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
